import React, { useState } from 'react';
import { useApp } from '../../context/AppContext';
import { X, KeyRound, LogIn, UserPlus } from 'lucide-react';
import { useToast } from '../ui/Toast';

export const AuthModal: React.FC = () => {
  const {
    authModalOpen,
    setAuthModalOpen,
    loginUser,
    registerUser,
  } = useApp();
  const { showToast } = useToast();

  const [tab, setTab] = useState<'login' | 'register'>('login');
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [mobile, setMobile] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (!authModalOpen) return null;

  const handleLoginSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    const result = await loginUser(identifier, password);
    setSubmitting(false);
    if (result.success) {
      showToast('Welcome back!', 'success');
      setAuthModalOpen(false);
    } else {
      setError(result.error || 'Invalid email/mobile or password.');
    }
  };

  const handleRegisterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!name.trim() || !email.trim() || !password) return;
    if (password.length < 6) {
      setError('Password must be at least 6 characters long.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setSubmitting(true);
    const result = await registerUser(name, email, mobile, password, confirmPassword);
    setSubmitting(false);

    if (result.success) {
      showToast(`Welcome, ${name}! Account registered successfully.`, 'success');
      setAuthModalOpen(false);
    } else {
      setError(result.error || 'Registration failed. Please check details.');
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#111111]/40 backdrop-blur-xs flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl max-w-md w-full p-6 sm:p-7 shadow-2xl border border-[#E5E9E9] animate-in fade-in zoom-in-95 duration-150">
        <div className="flex items-center justify-between pb-3 border-b border-[#E5E9E9] mb-4">
          <div className="flex items-center gap-2">
            <KeyRound className="w-5 h-5 text-[#1b8e88]" />
            <h3 className="text-base font-bold text-[#111111]">
              Account Authentication
            </h3>
          </div>
          <button
            onClick={() => setAuthModalOpen(false)}
            className="text-[#5F6368] hover:text-[#111111] p-1 rounded-lg cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tab switch */}
        <div className="flex rounded-xl bg-[#F0F4F4] p-1 mb-4 border border-[#E5E9E9]">
          <button
            onClick={() => {
              setTab('login');
              setError('');
            }}
            className={`flex-1 py-1.5 text-xs font-bold rounded-lg transition-all cursor-pointer ${
              tab === 'login'
                ? 'bg-white text-[#111111] shadow-xs'
                : 'text-[#5F6368] hover:text-[#111111]'
            }`}
          >
            Existing Login
          </button>
          <button
            onClick={() => {
              setTab('register');
              setError('');
            }}
            className={`flex-1 py-1.5 text-xs font-bold rounded-lg transition-all cursor-pointer ${
              tab === 'register'
                ? 'bg-white text-[#111111] shadow-xs'
                : 'text-[#5F6368] hover:text-[#111111]'
            }`}
          >
            Student Registration
          </button>
        </div>

        {error && (
          <div className="mb-4 p-2.5 rounded-xl bg-[#FDEDEF] border border-[#D94A5C]/30 text-xs text-[#D94A5C] font-medium">
            {error}
          </div>
        )}

        {tab === 'login' ? (
          <form onSubmit={handleLoginSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-bold text-[#111111] mb-1">
                Email Address or Mobile
              </label>
              <input
                type="text"
                required
                placeholder="e.g. student@mcqpractice.com or 9876543210"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                className="w-full px-3.5 py-2 rounded-xl border border-[#E5E9E9] text-xs focus:outline-none focus:border-[#56E8E1] bg-white"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-[#111111] mb-1">
                Password
              </label>
              <input
                type="password"
                required
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-3.5 py-2 rounded-xl border border-[#E5E9E9] text-xs focus:outline-none focus:border-[#56E8E1] bg-white"
              />
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="w-full py-2.5 bg-[#56E8E1] hover:bg-[#34d5cd] text-[#111111] rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-1.5 shadow-xs cursor-pointer disabled:opacity-50"
            >
              <LogIn className="w-4 h-4" />
              {submitting ? 'Signing In...' : 'Sign In'}
            </button>
          </form>
        ) : (
          <form onSubmit={handleRegisterSubmit} className="space-y-3">
            <div>
              <label className="block text-xs font-bold text-[#111111] mb-1">
                Full Name *
              </label>
              <input
                type="text"
                required
                placeholder="e.g. John Doe"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-3.5 py-2 rounded-xl border border-[#E5E9E9] text-xs focus:outline-none focus:border-[#56E8E1] bg-white"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-[#111111] mb-1">
                Email Address *
              </label>
              <input
                type="email"
                required
                placeholder="student@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-3.5 py-2 rounded-xl border border-[#E5E9E9] text-xs focus:outline-none focus:border-[#56E8E1] bg-white"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-[#111111] mb-1">
                Mobile Number (Optional)
              </label>
              <input
                type="tel"
                placeholder="e.g. 9876543210"
                value={mobile}
                onChange={(e) => setMobile(e.target.value)}
                className="w-full px-3.5 py-2 rounded-xl border border-[#E5E9E9] text-xs focus:outline-none focus:border-[#56E8E1] bg-white"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-[#111111] mb-1">
                Password * (min. 6 characters)
              </label>
              <input
                type="password"
                required
                minLength={6}
                placeholder="Create a strong password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-3.5 py-2 rounded-xl border border-[#E5E9E9] text-xs focus:outline-none focus:border-[#56E8E1] bg-white"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-[#111111] mb-1">
                Confirm Password *
              </label>
              <input
                type="password"
                required
                minLength={6}
                placeholder="Re-enter password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full px-3.5 py-2 rounded-xl border border-[#E5E9E9] text-xs focus:outline-none focus:border-[#56E8E1] bg-white"
              />
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="w-full py-2.5 bg-[#56E8E1] hover:bg-[#34d5cd] text-[#111111] rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-1.5 shadow-xs cursor-pointer disabled:opacity-50 mt-2"
            >
              <UserPlus className="w-4 h-4" />
              {submitting ? 'Creating Account...' : 'Create Account'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};
